import { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
    conductores,
    gruposVacaciones,
    gruposLibranzas,
    festivos,
    anexoLibranzas,
    motivos
} from '../services/api';
import { getTurnoForDate as dateUtilsGetTurno, normalizeTurno, isPartialHolidayForTurno } from '../utils/dateUtils';
import {
    ChevronLeft,
    ChevronRight,
    Save,
    Trash2,
    User,
    Users,
    Calendar as CalendarIcon,
    Euro,
    Briefcase
} from 'lucide-react';
import { useToast } from '../context/ToastContext';

// Helper for dates
const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

const MONTHS = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const WEEKDAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

// Utilities for date manipulation - use UTC to avoid timezone shift
const formatDateDTO = (date) => {
    const d = new Date(date);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
};

// --- LOGIC FOR SHIFTS AND GROUPS ---
const getDayType = (year, month, day, dateStr, options = {}) => {
    const {
        manualLibranzas = [], // Array of date strings
        vacationGroups = [],
        selectedConductorId = null,
        festivos = {},
        conductorDiasEspeciales = {},
        libranzaDates = new Set(), // Set of date strings (Manual + Group + Partial Holidays)
        partialHolidayDates = new Set() // Dates that are partial holidays for this conductor
    } = options;

    if (options.conductorVacationDates && options.conductorVacationDates.has(dateStr)) {
        return 'vacation';
    }

    if (libranzaDates.has(dateStr) || partialHolidayDates.has(dateStr)) {
        return 'libranza';
    }

    return 'work';
};

// Calculate Shift (Mañana/Tarde) - Using unified dateUtils module
const getTurnoForDate = (targetDateStr, initialTurno, referenceDateStr = '2026-01-01') => {
    return dateUtilsGetTurno(targetDateStr, initialTurno);
};

// === GLOBAL DAY VIEW COMPONENT ===
// Shows today's overview for all conductors with libranzas and vacaciones
const GlobalDayView = ({ turnos, tipoFilter }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const isSunday = today.getDay() === 0;
    const dayName = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'][today.getDay()];
    const monthName = MONTHS[today.getMonth()];

    // Fetch global status on mount
    useEffect(() => {
        const fetchGlobalStatus = async () => {
            try {
                setLoading(true);
                // Dynamic import to avoid circular dependency
                const { admin } = await import('../services/api');
                const tipoQuery = tipoFilter === 'todos' ? null : tipoFilter;
                const status = await admin.getGlobalDayStatus(todayStr, tipoQuery);
                setData(status);
            } catch (err) {
                console.error('Error fetching global day status:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchGlobalStatus();
    }, [todayStr, tipoFilter]);

    const turnoMColor = turnos.find(t => t.nombre === 'Mañana')?.color_bg || '#3b82f6';
    const turnoTColor = turnos.find(t => t.nombre === 'Tarde')?.color_bg || '#8b5cf6';
    const libranzaColor = turnos.find(t => t.nombre === 'Libranza')?.color_bg || '#22c55e';
    const vacacionesColor = turnos.find(t => t.nombre === 'Vacaciones')?.color_bg || '#ef4444';

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="w-8 h-8 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
                <div className="text-gray-400 animate-pulse">Cargando estado global...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="card p-6 text-center text-red-400">
                <p>Error al cargar datos: {error}</p>
            </div>
        );
    }

    if (!data) return null;

    return (
        <div className="space-y-6 animate-slide-in">
            {/* Today Header */}
            <div className="card p-6 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border-cyan-500/20">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h2 className="text-2xl font-bold text-white">
                            {dayName}, {today.getDate()} de {monthName}
                        </h2>
                        <p className="text-gray-400 text-sm mt-1">
                            Vista global del servicio - Estado de todos los conductores
                            {data.festivo && <span className="ml-2 px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400 text-xs">🎉 {data.festivo.nombre}</span>}
                            {isSunday && !data.festivo && <span className="ml-2 px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400 text-xs">☀️ Domingo</span>}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3 text-sm">
                        <div className="text-center px-4 py-2 rounded-lg" style={{ background: `${turnoMColor}20` }}>
                            <div className="text-2xl font-bold" style={{ color: turnoMColor }}>{data.totals.workingM}</div>
                            <div className="text-gray-400">Mañana</div>
                        </div>
                        <div className="text-center px-4 py-2 rounded-lg" style={{ background: `${turnoTColor}20` }}>
                            <div className="text-2xl font-bold" style={{ color: turnoTColor }}>{data.totals.workingT}</div>
                            <div className="text-gray-400">Tarde</div>
                        </div>
                        <div className="text-center px-4 py-2 rounded-lg" style={{ background: `${libranzaColor}20` }}>
                            <div className="text-2xl font-bold" style={{ color: libranzaColor }}>{data.totals.libranza}</div>
                            <div className="text-gray-400">Libranza</div>
                        </div>
                        <div className="text-center px-4 py-2 rounded-lg" style={{ background: `${vacacionesColor}20` }}>
                            <div className="text-2xl font-bold" style={{ color: vacacionesColor }}>{data.totals.vacation}</div>
                            <div className="text-gray-400">Vacaciones</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Conductor Lists - Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                {/* Turno Mañana */}
                <div className="card p-5" style={{ borderColor: `${turnoMColor}40` }}>
                    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-white/10">
                        <div className="w-4 h-4 rounded-full" style={{ background: turnoMColor }}></div>
                        <h3 className="font-bold text-lg text-white">Turno Mañana</h3>
                        <span className="ml-auto text-sm px-2 py-1 rounded-full" style={{ background: `${turnoMColor}20`, color: turnoMColor }}>
                            {data.working.M.length}
                        </span>
                    </div>
                    <div className="space-y-1.5 max-h-72 overflow-y-auto">
                        {data.working.M.length === 0 ? (
                            <p className="text-gray-500 text-sm italic text-center py-4">Sin conductores</p>
                        ) : (
                            data.working.M.map(c => (
                                <div key={c.id} className="flex items-center gap-3 p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group relative">
                                    <span className="text-sm text-white truncate flex-1">{c.nombre}</span>
                                    {c.notas && c.notas.length > 0 && (
                                        <div className="flex gap-1">
                                            {c.notas.map((n, i) => (
                                                <div key={i} className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_5px_rgba(34,211,238,0.5)]" title={n.comentario}></div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Turno Tarde */}
                <div className="card p-5" style={{ borderColor: `${turnoTColor}40` }}>
                    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-white/10">
                        <div className="w-4 h-4 rounded-full" style={{ background: turnoTColor }}></div>
                        <h3 className="font-bold text-lg text-white">Turno Tarde</h3>
                        <span className="ml-auto text-sm px-2 py-1 rounded-full" style={{ background: `${turnoTColor}20`, color: turnoTColor }}>
                            {data.working.T.length}
                        </span>
                    </div>
                    <div className="space-y-1.5 max-h-72 overflow-y-auto">
                        {data.working.T.length === 0 ? (
                            <p className="text-gray-500 text-sm italic text-center py-4">Sin conductores</p>
                        ) : (
                            data.working.T.map(c => (
                                <div key={c.id} className="flex items-center gap-3 p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group relative">
                                    <span className="text-sm text-white truncate flex-1">{c.nombre}</span>
                                    {c.notas && c.notas.length > 0 && (
                                        <div className="flex gap-1">
                                            {c.notas.map((n, i) => (
                                                <div key={i} className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_5px_rgba(34,211,238,0.5)]" title={n.comentario}></div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Libranza */}
                <div className="card p-5" style={{ borderColor: `${libranzaColor}40` }}>
                    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-white/10">
                        <div className="w-4 h-4 rounded-full" style={{ background: libranzaColor }}></div>
                        <h3 className="font-bold text-lg text-white">Libranza</h3>
                        <span className="ml-auto text-sm px-2 py-1 rounded-full" style={{ background: `${libranzaColor}20`, color: libranzaColor }}>
                            {data.libranza.length}
                        </span>
                    </div>
                    <div className="space-y-1.5 max-h-72 overflow-y-auto">
                        {data.libranza.length === 0 ? (
                            <p className="text-gray-500 text-sm italic text-center py-4">Sin conductores</p>
                        ) : (
                            data.libranza.map(c => (
                                <div key={c.id} className="flex items-center gap-3 p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group relative">
                                    <span className="text-sm text-white truncate flex-1">{c.nombre}</span>
                                    <div className="flex items-center gap-1.5">
                                        {c.notas && c.notas.length > 0 && (
                                            <div className="flex gap-0.5">
                                                {c.notas.map((n, i) => (
                                                    <div key={i} className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_5px_rgba(34,211,238,0.5)]" title={n.comentario}></div>
                                                ))}
                                            </div>
                                        )}
                                        {c.reason && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-gray-400 whitespace-nowrap">
                                                {c.reason}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Vacaciones */}
                <div className="card p-5" style={{ borderColor: `${vacacionesColor}40` }}>
                    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-white/10">
                        <div className="w-4 h-4 rounded-full" style={{ background: vacacionesColor }}></div>
                        <h3 className="font-bold text-lg text-white">Vacaciones</h3>
                        <span className="ml-auto text-sm px-2 py-1 rounded-full" style={{ background: `${vacacionesColor}20`, color: vacacionesColor }}>
                            {data.vacation.length}
                        </span>
                    </div>
                    <div className="space-y-1.5 max-h-72 overflow-y-auto">
                        {data.vacation.length === 0 ? (
                            <p className="text-gray-500 text-sm italic text-center py-4">Sin conductores</p>
                        ) : (
                            data.vacation.map(c => (
                                <div key={c.id} className="flex items-center gap-3 p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group relative">
                                    <span className="text-sm text-white truncate flex-1">{c.nombre}</span>
                                    {c.notas && c.notas.length > 0 && (
                                        <div className="flex gap-1">
                                            {c.notas.map((n, i) => (
                                                <div key={i} className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_5px_rgba(34,211,238,0.5)]" title={n.comentario}></div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Summary footer */}
            <div className="text-center text-gray-500 text-xs">
                <p>📊 Total: {data.totals.total} conductores | Trabajando: {data.totals.working} | Libranza: {data.totals.libranza} | Vacaciones: {data.totals.vacation}</p>
            </div>
        </div>
    );
};

const Calendar = () => {
    const { user, isAdmin } = useAuth();
    const { error: showErrorToast } = useToast();

    // States
    const [viewMode, setViewMode] = useState('global');
    const [tipoFilter, setTipoFilter] = useState('todos');
    const [conductorList, setConductorList] = useState([]);
    const [selectedConductor, setSelectedConductor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [conductorLoading, setConductorLoading] = useState(false);

    // Data States
    const [data, setData] = useState({
        manualLibranzas: {},
        vacationGroups: [],
        libranzaGroups: [],
        festivos: {},
        conductoresGroups: {},
        turnos: []
    });

    // UNIFIED DATA from backend - SINGLE SOURCE OF TRUTH
    // When a conductor is selected, this contains their libranzas, vacaciones, and stats
    const [unifiedConductorData, setUnifiedConductorData] = useState(null);

    // Fetch Initial Data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            // 1. Fetch Conductores (Critical for Dropdown)
            try {
                const condRes = await conductores.getAll();
                const list = Array.isArray(condRes) ? condRes : [];
                setConductorList(list);

                // Set initial selection if not admin
                if (!isAdmin() && user?.conductor_id) {
                    setViewMode('conductor');
                    const myConductor = list.find(c => c.id === user.conductor_id);
                    setSelectedConductor(myConductor);
                }
            } catch (error) {
                console.error("Error loading conductores:", error);
                showErrorToast('Error al cargar la lista de conductores');
            }

            // 2. Fetch Calendar Data (Groups, Festivos, Anexo Libranzas)
            // NOTE: diasLibranza deprecated - data migrated to anexo_libranzas (migration 018)
            try {
                const [
                    gruposVacRes,
                    gruposLibRes,
                    festivosRes,
                    anexoLibranzasRes,
                    turnosRes
                ] = await Promise.all([
                    gruposVacaciones.getAll(),
                    gruposLibranzas.getAll(),
                    festivos.getAll(),
                    anexoLibranzas.getAll(2026),
                    motivos.getTurnos()
                ]);

                // Process Anexo Libranzas (individual days off)
                const libranzasMap = {};
                const anexoArray = Array.isArray(anexoLibranzasRes) ? anexoLibranzasRes : [];
                anexoArray.forEach(al => {
                    const cid = al.conductor_id;
                    if (!libranzasMap[cid]) libranzasMap[cid] = [];
                    const d = typeof al.fecha === 'string' ? al.fecha.split('T')[0] : formatDateDTO(al.fecha);
                    if (!libranzasMap[cid].includes(d)) {
                        libranzasMap[cid].push(d);
                    }
                });

                // Process Festivos - store both name and turno_afectado
                const festivosArray = festivosRes.festivos || (Array.isArray(festivosRes) ? festivosRes : []);
                const festivosMap = {};
                // Ensure festivosArray is array
                if (Array.isArray(festivosArray)) {
                    festivosArray.forEach(f => {
                        const d = typeof f.fecha === 'string' ? f.fecha.split('T')[0] : formatDateDTO(f.fecha);
                        festivosMap[d] = {
                            nombre: f.nombre,
                            turnoAfectado: f.turno_afectado || 'ambos'
                        };
                    });
                }

                // Process Groups
                const groupsVacArray = gruposVacRes.grupos || (Array.isArray(gruposVacRes) ? gruposVacRes : []);
                const groupsLibArray = gruposLibRes.grupos || (Array.isArray(gruposLibRes) ? gruposLibRes : []);

                setData({
                    manualLibranzas: libranzasMap,
                    vacationGroups: groupsVacArray,
                    libranzaGroups: groupsLibArray,
                    festivos: festivosMap,
                    conductoresGroups: {},
                    turnos: Array.isArray(turnosRes) ? turnosRes : []
                });

            } catch (error) {
                console.error("Error loading calendar data:", error);
                showErrorToast('Error al cargar datos del calendario');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [user?.conductor_id, user?.role]);


    // Unified Data Fetching Effect
    // Whenever selectedConductor changes, fetch their unified data (notes, stats, etc.)
    useEffect(() => {
        if (!selectedConductor) {
            setUnifiedConductorData(null);
            return;
        }

        const fetchUnifiedData = async () => {
            setConductorLoading(true);
            try {
                // Call the UNIFIED endpoint - SINGLE SOURCE OF TRUTH
                const year = 2026;
                const unifiedData = await conductores.getCalendario(selectedConductor.id, year);
                setUnifiedConductorData(unifiedData);
                console.log('[Calendar] Loaded unified data for', selectedConductor.nombre, unifiedData.stats);
            } catch (err) {
                console.error('Error loading conductor calendar data:', err);
                showErrorToast('Error al cargar datos del conductor');
                setUnifiedConductorData(null);
            } finally {
                setConductorLoading(false);
            }
        };

        fetchUnifiedData();
    }, [selectedConductor]);


    // Handle Conductor Selection from dropdown
    const handleConductorSelect = (conductorId) => {
        const conductor = conductorList.find(c => c.id === parseInt(conductorId));
        setSelectedConductor(conductor);
    };

    // Calculate Calendar Data for View
    // USES UNIFIED DATA from backend when available
    const calendarData = useMemo(() => {
        if (viewMode === 'conductor' && !selectedConductor) return null;

        const totalStats = { work: 0, libranza: 0, vacation: 0, festivo: 0 };
        const options = {
            festivos: data.festivos
        };

        // Prepare data specific to selected conductor
        if (viewMode === 'conductor' && selectedConductor) {

            // If we have unified data from backend, USE THAT (single source of truth)
            if (unifiedConductorData) {
                // Create Sets from the unified arrays for fast lookup
                options.conductorVacationDates = new Set(unifiedConductorData.vacaciones);
                options.libranzaDates = new Set(unifiedConductorData.libranzas);

                // Partial holidays - calculate from festivos like before
                const partialHolidaySet = new Set();
                const normalizedInitialTurno = normalizeTurno(selectedConductor.turno);
                const festivosList = Array.isArray(data.festivos) ? data.festivos : Object.entries(data.festivos).map(([k, v]) => ({ ...v, fecha: k }));

                festivosList.forEach(festivoInfo => {
                    const dateStr = typeof festivoInfo.fecha === 'string' ? festivoInfo.fecha.split('T')[0] : String(festivoInfo.fecha);
                    const dayTurno = getTurnoForDate(dateStr, normalizedInitialTurno);
                    if (isPartialHolidayForTurno(festivoInfo, dayTurno)) {
                        partialHolidaySet.add(dateStr);
                    }
                });
                options.partialHolidayDates = partialHolidaySet;
                options.initialTurno = selectedConductor.turno;

            } else {
                // Fallback to local calculation if unified data not loaded yet
                // (This is kept for backwards compatibility during loading)
                const vacDateSet = new Set();
                const libranzaDateSet = new Set();
                options.conductorVacationDates = vacDateSet;
                options.libranzaDates = libranzaDateSet;
                options.partialHolidayDates = new Set();
                options.initialTurno = selectedConductor.turno;
            }

        } else {
            return { months: null, stats: totalStats, hasSelection: false };
        }

        const year = 2026;
        const months = [];

        for (let m = 0; m < 12; m++) {
            const daysInMonth = new Date(year, m + 1, 0).getDate();
            const firstDay = new Date(year, m, 1).getDay();
            const startOffset = firstDay === 0 ? 6 : firstDay - 1;
            const days = [];

            for (let i = 0; i < startOffset; i++) {
                days.push({ key: `empty-${m}-${i}`, empty: true });
            }

            for (let day = 1; day <= daysInMonth; day++) {
                const dateStr = `${year}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

                const dayType = getDayType(year, m, day, dateStr, options);
                totalStats[dayType]++;

                let turno = null;
                let isPrima = false;

                const dateObj = new Date(dateStr);
                const isSunday = dateObj.getDay() === 0;
                const festivoInfo = data.festivos[dateStr];
                const isFestivo = !!(festivoInfo && festivoInfo.nombre);
                const isAnexo = unifiedConductorData
                    ? (unifiedConductorData.libranzasAnexo?.some(a => typeof a === 'object' ? a.fecha === dateStr : a === dateStr))
                    : (selectedConductor && data.manualLibranzas[selectedConductor.id]?.includes(dateStr));

                // NEW: General partial holiday indicator (for the red ribbon)
                const isPartialHoliday = !!(festivoInfo && festivoInfo.turnoAfectado && festivoInfo.turnoAfectado !== 'ambos');

                if (dayType === 'work') {
                    if (options.initialTurno) {
                        turno = getTurnoForDate(dateStr, options.initialTurno);
                    }
                    if (isSunday || isFestivo) {
                        isPrima = true;
                    }
                }
                days.push({
                    key: `${m}-${day}`,
                    day,
                    dateStr,
                    dayType,
                    turno,
                    isPrima,
                    isSunday,
                    isFestivo,
                    isPartialHoliday,
                    isAnexo,
                    notas: unifiedConductorData?.notas?.filter(n => n.fecha === dateStr) || [],
                    motivo: unifiedConductorData?.motivos?.[dateStr] || null,
                    empty: false
                });
            }

            months.push({ month: m, days });
        }

        return { months, stats: totalStats, hasSelection: true };

    }, [viewMode, selectedConductor, data, unifiedConductorData]);


    // CSS Classes for Days
    const getDayStyle = (dayData) => {
        if (dayData.empty) return { className: '', style: {} };

        const base = "relative overflow-hidden flex flex-col items-center justify-center rounded-lg text-sm font-semibold transition-all duration-200 cursor-default border border-transparent aspect-square";
        
        // Colores de referencia de la DB
        const vacColor = data.turnos.find(t => t.nombre === 'Vacaciones')?.color_bg || '#ef4444';
        const libColor = data.turnos.find(t => t.nombre === 'Libranza')?.color_bg || '#22c55e';
        const shiftColorData = data.turnos.find(t => t.nombre.charAt(0).toUpperCase() === dayData.turno);
        const workColor = shiftColorData?.color_bg || 'rgba(255, 255, 255, 0.1)';

        // Color base según el tipo de día (vacación > libranza > trabajo)
        let colorBase = workColor;
        if (dayData.dayType === 'vacation') colorBase = vacColor;
        else if (dayData.dayType === 'libranza') colorBase = libColor;

        // Comprobamos si el motivo es parcial (Reunión o Asamblea)
        const esParcial = dayData.motivo && (dayData.motivo.tipo_hora_sindical === 'reunion_sindical' || dayData.motivo.tipo_hora_sindical === 'asamblea');
        const colorMotivo = dayData.motivo?.motivo_color || '#8b5cf6';

        // Si es parcial, pintamos mitad y mitad con un degradado diagonal
        if (esParcial) {
            return {
                className: base,
                style: {
                    background: `linear-gradient(135deg, ${colorBase} 50%, ${colorMotivo} 50%)`,
                    color: '#ffffff',
                    boxShadow: `0 4px 12px ${colorMotivo}40`
                }
            };
        }

        // Caso normal (pintado completo)
        if (dayData.dayType === 'vacation') {
            return {
                className: base,
                style: {
                    background: `linear-gradient(to bottom right, ${vacColor}, ${vacColor}dd)`,
                    color: '#ffffff',
                    boxShadow: `0 4px 12px ${vacColor}40`
                }
            };
        }

        if (dayData.dayType === 'libranza') {
            return {
                className: base,
                style: {
                    background: `linear-gradient(to bottom right, ${libColor}, ${libColor}dd)`,
                    color: '#ffffff',
                    boxShadow: `0 4px 12px ${libColor}40`
                }
            };
        }

        if (dayData.dayType === 'work') {
            if (shiftColorData) {
                return {
                    className: base,
                    style: {
                        background: `linear-gradient(to bottom right, ${shiftColorData.color_bg}, ${shiftColorData.color_bg}dd)`,
                        color: shiftColorData.color_text || '#ffffff',
                        boxShadow: `0 4px 12px ${shiftColorData.color_bg}40`
                    }
                };
            }

            return {
                className: base,
                style: {
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#9ca3af',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                }
            };
        }

        return { className: base, style: {} };
    };

    // Handler for Interactions
    const handleDayClick = (dateStr, currentType) => {
        console.log("Clicked:", dateStr, currentType);
    };


    if (loading) return (
        <div className="flex flex-col items-center justify-center min-h-[500px] gap-4">
            <div className="w-8 h-8 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
            <div className="text-gray-400 animate-pulse">Cargando datos del calendario...</div>
        </div>
    );

    return (
        <div className="space-y-8 fade-in pb-12">
            {/* Header / Controls */}
            <div className="card flex flex-col md:flex-row justify-between items-start md:items-center p-6 gap-6 backdrop-blur-xl">
                <div>
                    <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                        Calendario 2026
                    </h1>
                    <p className="text-gray-400 text-sm mt-2 flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4 text-cyan-400" />
                        Gestión inteligente de turnos y libranzas
                    </p>
                </div>

                <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
                    {/* Mode Switcher */}
                    <div className="flex bg-black/20 p-1.5 rounded-xl border border-white/5">
                        {isAdmin() && (
                            <button
                                onClick={() => setViewMode('global')}
                                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${viewMode === 'global'
                                    ? 'bg-cyan-500/20 text-cyan-400 shadow-lg shadow-cyan-500/10 border border-cyan-500/20'
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                    }`}
                            >
                                Global
                            </button>
                        )}
                        <button
                            onClick={() => setViewMode('conductor')}
                            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${viewMode === 'conductor'
                                ? 'bg-cyan-500/20 text-cyan-400 shadow-lg shadow-cyan-500/10 border border-cyan-500/20'
                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                        >
                            Conductor
                        </button>
                    </div>

                    {/* Tipo Filter */}
                    <div className="flex bg-black/20 p-1.5 rounded-xl border border-white/5 mx-2">
                        <button
                            onClick={() => setTipoFilter('todos')}
                            className={`px-4 py-2 text-sm rounded-md transition-all font-semibold ${tipoFilter === 'todos' ? 'bg-gray-600 text-white shadow' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                        >
                            Todos
                        </button>
                        <button
                            onClick={() => setTipoFilter('bei')}
                            className={`px-4 py-2 text-sm rounded-md transition-all font-semibold ${tipoFilter === 'bei' ? 'bg-cyan-500 text-white shadow' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                        >
                            🚌 BEI
                        </button>
                        <button
                            onClick={() => setTipoFilter('servicio')}
                            className={`px-4 py-2 text-sm rounded-md transition-all font-semibold ${tipoFilter === 'servicio' ? 'bg-blue-500 text-white shadow' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                        >
                            🚍 Servicio
                        </button>
                    </div>

                    {/* Conductor Selector */}
                    {viewMode === 'conductor' && (
                        <div className="relative group">
                            <select
                                className="appearance-none bg-black/20 border border-white/10 text-white text-sm rounded-xl focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 block w-full md:w-72 p-3 pl-11 transition-all hover:bg-black/30 cursor-pointer"
                                value={selectedConductor?.id || ''}
                                onChange={(e) => handleConductorSelect(e.target.value)}
                                disabled={!isAdmin() && user?.role !== 'admin'}
                            >
                                <option value="" className="bg-gray-900 text-gray-400">Seleccionar Conductor...</option>
                                {conductorList
                                    .filter(c => tipoFilter === 'todos' || c.tipo === tipoFilter)
                                    .map(c => (
                                        <option key={c.id} value={c.id} className="bg-gray-900">
                                            {c.numero} - {c.nombre} {c.apellidos || ''}
                                        </option>
                                    ))}
                            </select>
                            <User className="absolute left-3.5 top-3.5 w-4 h-4 text-cyan-400 group-hover:scale-110 transition-transform" />
                            <ChevronRight className="absolute right-3.5 top-3.5 w-4 h-4 text-gray-500 rotate-90 pointer-events-none" />
                        </div>
                    )}
                </div>
            </div>

            {/* Statistics Cards */}
            {calendarData?.hasSelection && (() => {
                // Use unified stats from backend - SINGLE SOURCE OF TRUTH
                const displayStats = unifiedConductorData?.stats ? {
                    work: unifiedConductorData.stats.totalWorkDays,
                    libranza: unifiedConductorData.stats.totalLibranzas,
                    vacation: unifiedConductorData.stats.totalVacaciones
                } : calendarData.stats;

                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="card relative overflow-hidden group hover:border-blue-500/30 transition-all p-5">
                            <div className="absolute right-0 top-0 w-24 h-24 bg-blue-500/10 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
                            <div className="relative z-10 flex items-center gap-4">
                                <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 text-blue-400 ring-1 ring-blue-500/30">
                                    <Briefcase className="w-6 h-6" />
                                </div>
                                <div>
                                    <div className="text-sm text-gray-400 font-medium">Laborables</div>
                                    <div className="text-3xl font-bold text-white mt-1 tracking-tight">
                                        {displayStats.work}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card relative overflow-hidden group hover:border-green-500/30 transition-all p-5">
                            <div className="absolute right-0 top-0 w-24 h-24 bg-green-500/10 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
                            <div className="relative z-10 flex items-center gap-4">
                                <div className="p-3 rounded-xl bg-gradient-to-br from-green-500/20 to-green-600/20 text-green-400 ring-1 ring-green-500/30">
                                    <Users className="w-6 h-6" />
                                </div>
                                <div>
                                    <div className="text-sm text-gray-400 font-medium">Libranzas</div>
                                    <div className="text-3xl font-bold text-white mt-1 tracking-tight">
                                        {displayStats.libranza}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card relative overflow-hidden group hover:border-red-500/30 transition-all p-5">
                            <div className="absolute right-0 top-0 w-24 h-24 bg-red-500/10 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
                            <div className="relative z-10 flex items-center gap-4">
                                <div className="p-3 rounded-xl bg-gradient-to-br from-red-500/20 to-red-600/20 text-red-400 ring-1 ring-red-500/30">
                                    <User className="w-6 h-6" />
                                </div>
                                <div>
                                    <div className="text-sm text-gray-400 font-medium">Vacaciones</div>
                                    <div className="text-3xl font-bold text-white mt-1 tracking-tight">
                                        {displayStats.vacation}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Calendar Grid */}
            {calendarData?.hasSelection ? (
                <div className="space-y-6 animate-slide-in">
                    {/* Legend */}
                    <div className="card flex flex-wrap justify-center gap-6 p-4 backdrop-blur-md bg-white/5 border-white/10">
                        {data.turnos.map(turno => (
                            <div key={turno.id} className="flex items-center gap-2.5">
                                <div
                                    className="w-3 h-3 rounded-full shadow-lg"
                                    style={{
                                        background: `linear-gradient(to right, ${turno.color_bg}, ${turno.color_bg}dd)`,
                                        boxShadow: `0 0 10px ${turno.color_bg}60`
                                    }}
                                ></div>
                                <span className="text-sm text-gray-300">{turno.nombre}</span>
                            </div>
                        ))}
                        <div className="flex items-center gap-2.5">
                            <div
                                className="w-3 h-3 rounded-full shadow-lg"
                                style={{
                                    background: `linear-gradient(to right, ${(data.turnos.find(t => t.nombre === 'Vacaciones') || { color_bg: '#ef4444' }).color_bg}, ${(data.turnos.find(t => t.nombre === 'Vacaciones') || { color_bg: '#ef4444' }).color_bg}dd)`,
                                    boxShadow: `0 0 10px ${(data.turnos.find(t => t.nombre === 'Vacaciones') || { color_bg: '#ef4444' }).color_bg}60`
                                }}
                            ></div>
                            <span className="text-sm text-gray-300">Vacaciones</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                            <div
                                className="w-3 h-3 rounded-full shadow-lg"
                                style={{
                                    background: `linear-gradient(to right, ${(data.turnos.find(t => t.nombre === 'Libranza') || { color_bg: '#22c55e' }).color_bg}, ${(data.turnos.find(t => t.nombre === 'Libranza') || { color_bg: '#22c55e' }).color_bg}dd)`,
                                    boxShadow: `0 0 10px ${(data.turnos.find(t => t.nombre === 'Libranza') || { color_bg: '#22c55e' }).color_bg}60`
                                }}
                            ></div>
                            <span className="text-sm text-gray-300">Libranza</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                            <div className="relative w-3.5 h-3.5 rounded bg-gray-600 overflow-hidden border border-white/10">
                                <div className="absolute top-0 right-0 w-0 h-0 border-t-[7px] border-t-yellow-400 border-l-[7px] border-l-transparent"></div>
                            </div>
                            <span className="text-sm text-gray-300">Festivo/Dom.</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                            <div className="relative w-3.5 h-3.5 rounded bg-gray-600 overflow-hidden border border-white/10">
                                <div className="absolute top-0 left-0 w-0 h-0 border-t-[7px] border-t-red-500 border-r-[7px] border-r-transparent"></div>
                            </div>
                            <span className="text-sm text-gray-300">F. Parcial</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                            <div className="relative w-3.5 h-3.5 rounded bg-gray-600 overflow-hidden border border-white/10">
                                <div className="absolute bottom-0 left-0 w-0 h-0 border-b-[7px] border-b-orange-500 border-r-[7px] border-r-transparent"></div>
                            </div>
                            <span className="text-sm text-gray-300">Día Libre Ind.</span>
                        </div>
                        <div className="flex items-center gap-2.5 px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/20">
                            <Euro className="w-3.5 h-3.5 text-yellow-400" />
                            <span className="text-sm text-yellow-200">Prima</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {calendarData.months.map((monthData) => (
                            <div key={monthData.month} className="card group hover:border-cyan-500/30 transition-all duration-300 p-0 overflow-hidden bg-[#0a0a16]/40 backdrop-blur-sm">
                                {/* Month Header */}
                                <div className="bg-white/5 px-4 py-4 border-b border-white/5 flex justify-between items-center">
                                    <h3 className="font-bold text-lg text-white capitalize tracking-wide">
                                        {MONTHS[monthData.month]}
                                    </h3>
                                    <span className="text-xs font-mono text-gray-500 bg-black/20 px-2 py-1 rounded">2026</span>
                                </div>

                                <div className="p-5">
                                    {/* Weekdays Header */}
                                    <div className="grid grid-cols-7 mb-3">
                                        {WEEKDAYS.map(d => (
                                            <div key={d} className="text-center text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                                                {d}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Days Grid */}
                                    <div className="grid grid-cols-7 gap-1.5">
                                        {monthData.days.map((dayData, idx) => (
                                            dayData.empty ? (
                                                <div key={`empty-${monthData.month}-${idx}`} className="aspect-square"></div>
                                            ) : (
                                                <div
                                                    key={dayData.key}
                                                    className={`
                                                        ${getDayStyle(dayData).className}
                                                        ${dayData.isPrima ? 'ring-1 ring-yellow-400/50 shadow-[0_0_10px_rgba(250,204,21,0.2)]' : ''}
                                                    `}
                                                    style={getDayStyle(dayData).style}
                                                    title={dayData.dateStr}
                                                >
                                                    {/* Yellow ribbon for festivos/domingos (TOP-RIGHT) */}
                                                    {(dayData.isFestivo || dayData.isSunday) && (
                                                        <div className="absolute top-0 right-0 w-0 h-0 
                                                            border-t-[10px] border-t-yellow-400 
                                                            border-l-[10px] border-l-transparent z-10"></div>
                                                    )}
                                                    {/* Red ribbon for partial holidays (festivo parcial) - TOP-LEFT */}
                                                    {dayData.isPartialHoliday && (
                                                        <div className="absolute top-0 left-0 w-0 h-0 
                                                            border-t-[10px] border-t-red-500 
                                                            border-r-[10px] border-r-transparent z-10"></div>
                                                    )}
                                                    {/* Orange ribbon for Anexos (Individual days off) - BOTTOM-LEFT */}
                                                    {dayData.isAnexo && (
                                                        <div className="absolute bottom-0 left-0 w-0 h-0 
                                                            border-b-[10px] border-b-orange-500 
                                                            border-r-[10px] border-r-transparent z-10"></div>
                                                    )}

                                                    {/* Prima Icon - Only if working on Festivo/Sunday and NO Euro icon requested for Sundays */}
                                                    {/* User wanted to take it off from Sundays, but maybe keep the small badge for Prima logic if it's NOT a Sunday? */}
                                                    {/* For now, removing the Euro icon as requested: "los domingos tienen un icono de euro, eso hay que quitarlo" */}
                                                    {dayData.isPrima && (
                                                        <div className="absolute top-0.5 right-0.5 text-[8px] z-20 opacity-80">
                                                            {/* We don't show the Euro icon here anymore per user request */}
                                                        </div>
                                                    )}

                                                    <span className="relative z-0">{dayData.day}</span>

                                                    {/* Notes Indicator */}
                                                    {dayData.notas && dayData.notas.length > 0 && (
                                                        <div className="absolute top-0.5 left-0.5 flex gap-0.5 z-20">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_5px_rgba(34,211,238,0.8)]"></div>
                                                            {dayData.notas.length > 1 && (
                                                                <div className="w-1 h-1 rounded-full bg-cyan-400/60 transition-opacity"></div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : viewMode === 'global' ? (
                /* GLOBAL VIEW - Shows today's overview for all conductors */
                <GlobalDayView
                    turnos={data.turnos}
                    tipoFilter={tipoFilter}
                />
            ) : (
                <div className="flex flex-col items-center justify-center min-h-[400px] card border-dashed border-2 border-white/10 bg-black/20">
                    <div className="p-6 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 shadow-xl mb-6 ring-1 ring-white/10">
                        <User className="w-12 h-12 text-gray-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">Selecciona un conductor</h3>
                    <p className="text-gray-400 max-w-sm text-center leading-relaxed">
                        Elige un conductor del menú superior para visualizar su calendario laboral completo, incluyendo turnos, libranzas y vacaciones.
                    </p>
                </div>
            )}
        </div>
    );
};

export default Calendar;
